if (!customElements.get("sticky-atc-bar")) {
  customElements.define(
    "sticky-atc-bar",
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add("sticky-atc-bar-enabled");

        this.selectors = {
          variantIdSelect: '[name="id"]',
          quantityInput: ".f-quantity__input",
          quantityMinus: '.f-quantity__button[name="minus"]',
          quantityPlus: '.f-quantity__button[name="plus"]',
        };
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get quantityInput() {
        return this.querySelector(this.selectors.quantityInput);
      }

      connectedCallback() {
        this.addToCartLabel =
          this.dataset.buttonLabel || window.FoxThemeStrings.addToCart;
        this.showPrice = this.dataset.showPrice === "true";
        this.productFormActions = document.querySelector(
          ".f-main-product-form"
        );
        this.container = this.closest(".sticky-atc-bar");
        this.mainProduct = document.querySelector(
          "product-info[id^='MainProduct']"
        );

        this.variantData = this.getVariantData();
        this.select = this.querySelector("select");

        const initialVariantId = Number(
          this.querySelector(this.selectors.variantIdSelect)?.value
        );
        this.currentVariant = this.variantData.find(
          (variant) => variant.id === initialVariantId
        );

        this.init();
        this.bindQuantityEvents();
        this.updateQuantityButtonsState();
        this.syncQuantityWithMainForm();
        this.syncSellingPlanWithMainForm();
        this.attachSubmitPriceForwarder();
        this.attachStickySubmitForwarding();
        this.attachAddonPriceSync();

        this.select.addEventListener("change", () => {
          const mainVariantPicker =
            this.mainProduct.querySelector("variant-picker");
          const selectedVariantId = this.querySelector(
            this.selectors.variantIdSelect
          ).value;
          this.currentVariant = this.variantData.find(
            (variant) => variant.id === Number(selectedVariantId)
          );

          if (mainVariantPicker) {
            Array.from(
              mainVariantPicker.querySelectorAll(
                "select, .variant-picker__option-values"
              ),
              (element, index) => {
                const variantOptionVal = this.currentVariant.options[index];
                switch (element.tagName) {
                  case "SELECT":
                    element.value = variantOptionVal;
                    break;
                  case "DIV":
                    Array.from(element.querySelectorAll("input")).forEach(
                      (radio) => {
                        if (radio.value === variantOptionVal) {
                          radio.checked = true;
                        }
                      }
                    );
                    break;
                }
              }
            );
            mainVariantPicker.dispatchEvent(new Event("change"));
          }

          this.updateButtonText();
          this.updateButton(true, "", false);
          if (!this.currentVariant) {
            this.updateButton(true, "", true);
          } else {
            this.updateButton(
              !this.currentVariant.available,
              window.FoxThemeStrings.soldOut
            );
          }
        });
      }

      getVariantData() {
        this.variantData =
          this.variantData ||
          JSON.parse(
            this.container.querySelector('[type="application/json"]')
              .textContent
          );
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add("sticky-atc-bar--show");
          return;
        }
        this.productId = this.dataset.productId;
        const isMobile = window.matchMedia("(max-width: 639px)");

        try {
          // Chrome & Firefox
          isMobile.addEventListener("change", this.checkDevice.bind(this));
        } catch (err) {
          try {
            // Safari
            isMobile.addListener(this.checkDevice.bind(this));
          } catch (e2) {
            console.error(e2);
          }
        }

        // Initial check
        this.checkDevice(isMobile);

        const headerHeight = window.FoxThemeSettings.headerHeight || 80;
        const rootMargin = `-${headerHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Use the target's actual position rather than its visibility
              // ratio: on load the button can be only partially on-screen
              // (below the fold), which would otherwise read as "not fully
              // visible" and show the bar before the user has scrolled at all.
              const scrolledPast = entry.boundingClientRect.top < headerHeight;
              const method = scrolledPast ? "add" : "remove";
              this.container.classList[method]("sticky-atc-bar--show");
            });
          },
          { threshold: 0, rootMargin }
        );
        this.setObserveTarget();
        this.syncWithMainProductForm();
      }

      setObserveTarget() {
        this.observer.observe(this.productFormActions);
        this.observeTarget = this.productFormActions;
      }

      checkDevice(e) {
        setTimeout(() => {
          const sectionHeight = this.clientHeight + "px";
          document.documentElement.style.setProperty(
            "--f-sticky-atc-bar-height",
            sectionHeight
          );
        }, 100);
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(
          "product-form-sticky-atc-bar"
        );
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');

        if (!addButton) return;

        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (text) {
            const addButtonText = productForm.querySelector(
              '[name="add"] > span:not(.f-icon)'
            );
            if (addButtonText) addButtonText.textContent = text;
          }
        } else {
          addButton.removeAttribute("disabled");
          this.updateButtonText();
        }
      }

      // Mirrors the main product page's "Label – $Total" button text,
      // recomputed locally so it stays scoped to this bar's own quantity
      // and variant state.
      updateButtonText() {
        const productForm = document.getElementById(
          "product-form-sticky-atc-bar"
        );
        if (!productForm) return;
        const addButtonText = productForm.querySelector(
          '[name="add"] > span:not(.f-icon)'
        );
        if (!addButtonText) return;

        if (!this.showPrice || !this.currentVariant) {
          addButtonText.textContent = this.addToCartLabel;
          return;
        }

        const quantity = parseInt(this.quantityInput?.value, 10) || 1;
        const skioUnitPrice = this.getSkioUnitPrice();
        const unitPrice =
          skioUnitPrice !== null ? skioUnitPrice : this.currentVariant.price;
        const total = unitPrice * quantity + this.getSelectedAddonsTotal();
        const money_format = window.FoxThemeSettings.money_format;
        addButtonText.textContent = `${this.addToCartLabel} – ${formatMoney(
          total,
          money_format
        )}`;
      }

      updateQuantityInput() {
        const selectedVariantId = this.querySelector(
          this.selectors.variantIdSelect
        ).value;
        this.currentVariant = this.variantData.find(
          (variant) => variant.id === Number(selectedVariantId)
        );
        fetch(
          `${this.getAttribute("data-url")}?variant=${
            this.currentVariant.id
          }&section_id=${this.sectionId}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const quantity = document.querySelector(
              `#QuantitySticky-${this.sectionId}`
            );
            const newQuantity = parsedHTML.querySelector(
              `#QuantitySticky-${this.sectionId}`
            );
            if (newQuantity) {
              quantity.innerHTML = newQuantity.innerHTML;
            }
            this.updateQuantityButtonsState();
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {});
      }

      // ====== Quantity stepper ======
      // Self-contained: doesn't rely on the shared <quantity-input> custom
      // element, since that tag is also targeted by a separate site-wide
      // script (Custom.js) that assumes only one such element exists on the
      // page. Bound once via delegation on `this` so it keeps working after
      // updateQuantityInput() swaps the inner markup on a variant change.
      getQuantityBounds() {
        const input = this.quantityInput;
        if (!input) return { min: 1, max: null, step: 1 };
        const min =
          parseInt(input.getAttribute("data-min") || input.min, 10) || 1;
        const max = input.hasAttribute("data-max")
          ? parseInt(input.getAttribute("data-max"), 10)
          : null;
        const step = parseInt(input.getAttribute("step"), 10) || 1;
        return { min, max, step };
      }

      setQuantity(value) {
        const input = this.quantityInput;
        if (!input) return;
        const { min, max } = this.getQuantityBounds();
        let next = Math.max(value, min);
        if (max !== null) next = Math.min(next, max);
        if (input.value !== String(next)) input.value = next;
        this.updateQuantityButtonsState();
        this.updateButtonText();
        this.pushQuantityToMainForm(next);
      }

      updateQuantityButtonsState() {
        const input = this.quantityInput;
        if (!input) return;
        const { min, max } = this.getQuantityBounds();
        const value = parseInt(input.value, 10) || min;
        const minusButton = this.querySelector(this.selectors.quantityMinus);
        const plusButton = this.querySelector(this.selectors.quantityPlus);
        if (minusButton) minusButton.toggleAttribute("disabled", value <= min);
        if (plusButton)
          plusButton.toggleAttribute(
            "disabled",
            max !== null && value >= max
          );
      }

      bindQuantityEvents() {
        this.addEventListener("click", (event) => {
          const button = event.target.closest(".f-quantity__button");
          if (!button) return;
          event.preventDefault();
          const input = this.quantityInput;
          if (!input) return;
          const { step } = this.getQuantityBounds();
          const current = parseInt(input.value, 10) || 1;
          if (button.name === "plus") {
            this.setQuantity(current + step);
          } else if (button.name === "minus") {
            this.setQuantity(current - step);
          }
        });

        this.addEventListener("change", (event) => {
          if (!event.target.matches(this.selectors.quantityInput)) return;
          const value = parseInt(event.target.value, 10);
          this.setQuantity(isNaN(value) ? 1 : value);
        });
      }

      syncWithMainProductForm() {
        const variantInput = this.querySelector('[name="id"]');
        window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.variantChange, (evt) => {
          const isMainProduct =
            evt.data.sectionId === this.mainProduct.dataset.section;
          if (!isMainProduct) return;
          this.currentVariant = evt.data.variant;
          if (this.currentVariant) {
            this.container.classList.remove("f-hidden");
            variantInput.value = evt.data.variant.id;
            this.updateButtonText();
            this.updateButton(true, "", false);
            if (!evt.data.variant) {
              this.updateButton(true, "", false);
            } else {
              this.updateButton(
                !evt.data.variant.available,
                window.FoxThemeStrings.soldOut
              );
            }
          } else {
            this.container.classList.add("f-hidden");
          }

          this.updateQuantityInput();
        });
      }

      // ====== Quantity <-> main product form sync ======
      // Keeps this bar's quantity and the main buy box's quantity mirroring
      // each other, so changing either one updates the other.
      getMainQuantityInput() {
        const inputs = document.querySelectorAll(
          'quantity-input input[name="quantity"]'
        );
        for (const el of inputs) {
          if (!el.closest(".sticky-atc-bar")) return el;
        }
        return null;
      }

      pushQuantityToMainForm(value) {
        if (this._syncingQuantity) return;
        const mainInput = this.getMainQuantityInput();
        if (!mainInput || mainInput.value === String(value)) return;
        this._syncingQuantity = true;
        mainInput.value = value;
        mainInput.dispatchEvent(new Event("input", { bubbles: true }));
        mainInput.dispatchEvent(new Event("change", { bubbles: true }));
        this._syncingQuantity = false;
      }

      handleMainQuantityChange(event) {
        if (this._syncingQuantity) return;
        const target = event.target;
        if (
          !target ||
          target.name !== "quantity" ||
          !target.closest("quantity-input") ||
          target.closest(".sticky-atc-bar")
        ) {
          return;
        }
        const value = parseInt(target.value, 10);
        if (isNaN(value)) return;
        this._syncingQuantity = true;
        this.setQuantity(value);
        this._syncingQuantity = false;
      }

      syncQuantityWithMainForm() {
        const mainInput = this.getMainQuantityInput();
        if (mainInput) {
          const value = parseInt(mainInput.value, 10);
          if (!isNaN(value)) this.setQuantity(value);
        }
        // Delegated at the document level (rather than bound to the input
        // directly) so the sync survives the main quantity block being
        // re-rendered, e.g. on a variant change.
        document.addEventListener(
          "input",
          (event) => this.handleMainQuantityChange(event),
          true
        );
        document.addEventListener(
          "change",
          (event) => this.handleMainQuantityChange(event),
          true
        );
      }

      // ====== Subscription (selling plan) sync ======
      // The subscribe/one-time picker itself (Skio) only exists in the main
      // buy box - this bar doesn't duplicate that UI. Instead it watches
      // whichever plan is currently selected there and mirrors it: the
      // sticky button's own hidden `selling_plan` field is kept in step so
      // adding from the bar creates the same purchase type, and the price
      // shown updates to match (reading the same price the Skio widget
      // already displays, the same way the main Add to Bag button does).
      getMainSellingPlanInput() {
        const inputs = document.querySelectorAll('input[name="selling_plan"]');
        for (const el of inputs) {
          if (!el.closest(".sticky-atc-bar")) return el;
        }
        return null;
      }

      getOwnSellingPlanInput() {
        return this.querySelector('input[name="selling_plan"]');
      }

      applySellingPlanState() {
        const mainInput = this._mainSellingPlanInput;
        if (!mainInput) return;
        const planId = (mainInput.value || "").trim();

        const ownInput = this.getOwnSellingPlanInput();
        if (ownInput) ownInput.value = planId;

        this.currentSellingPlanId = planId;
        this.updateButtonText();
      }

      syncSellingPlanWithMainForm() {
        const trySetup = () => {
          const mainInput = this.getMainSellingPlanInput();
          if (!mainInput) return false;

          this._mainSellingPlanInput = mainInput;
          this.applySellingPlanState();
          this.attachSkioPriceObservers();

          // Skio's picker inputs fire a bubbling "change" event (see the
          // preselect-purchase snippet elsewhere in the theme), so this
          // catches selection immediately...
          document.addEventListener(
            "change",
            (event) => {
              const target = event.target;
              if (
                target &&
                target.closest &&
                target.closest('fieldset[skio-plan-picker]')
              ) {
                this.applySellingPlanState();
              }
            },
            true
          );

          // ...and this is a fallback for cases where Skio updates the
          // hidden field's value from its own script without a bubbling
          // event - mirrors how the theme's own Skio price sync (Custom.js)
          // already has to poll for the same reason.
          new MutationObserver(() => this.applySellingPlanState()).observe(
            mainInput,
            { attributes: true, attributeFilter: ["value"] }
          );
          let lastValue = mainInput.value;
          setInterval(() => {
            if (mainInput.value !== lastValue) {
              lastValue = mainInput.value;
              this.applySellingPlanState();
            }
          }, 300);

          return true;
        };

        if (!trySetup()) {
          // Skio's block can render its picker (and this hidden field)
          // slightly after first paint - poll briefly until it shows up.
          let tries = 0;
          const poll = setInterval(() => {
            tries++;
            if (trySetup() || tries > 200) clearInterval(poll);
          }, 100);
        }
      }

      // Skio can also update its displayed price (e.g. after finishing an
      // async calculation) without the selected plan itself changing -
      // watch those spans directly too, same as Custom.js's price sync
      // does for the main button.
      attachSkioPriceObservers() {
        const subEl = document.querySelector("span[skio-subscription-price]");
        const oneEl = document.querySelector("span[skio-onetime-price]");
        const onChange = () => this.updateButtonText();
        const observer = new MutationObserver(onChange);
        if (subEl) {
          observer.observe(subEl, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
        if (oneEl) {
          observer.observe(oneEl, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      // Reads the unit price Skio is currently displaying for the selected
      // plan (subscription vs one-time), same source the main Add to Bag
      // button's price sync (Custom.js) uses. Returns null when there's no
      // subscription widget on this product, so callers fall back to the
      // plain variant price.
      getSkioUnitPrice() {
        const extractCents = (text) => {
          const match = (text || "").trim().match(/([\d.,]+)/);
          if (!match) return null;
          return Math.round(parseFloat(match[1].replace(/,/g, "")) * 100);
        };

        if (this.currentSellingPlanId) {
          const subEl = document.querySelector("span[skio-subscription-price]");
          const subPrice = subEl ? extractCents(subEl.textContent) : null;
          if (subPrice !== null) return subPrice;
        }

        const oneEl = document.querySelector("span[skio-onetime-price]");
        return oneEl ? extractCents(oneEl.textContent) : null;
      }

      // ====== Add-on price sync ======
      // "Pairs Well With" style upsell widgets (e.g. Selleasy) render their
      // own checkbox + quantity cards outside this bar entirely. When one
      // is checked, this bar's own "Add to Bag - $X" text should include
      // that add-on's price, the same way the main button's price already
      // does (see Custom.js). Reads everything fresh from the live DOM
      // each time rather than tracking selection state locally, since the
      // widget re-renders its own cards independently (a stateful map
      // keyed by node references would go stale the moment a card gets
      // replaced).
      getSelectedAddonsTotal() {
        // Returns cents, matching this bar's own price units (Shopify
        // variant prices, and the money_format-based formatter below).
        let totalCents = 0;
        document
          .querySelectorAll(".lb-flat-card-host, .lb-product-card")
          .forEach((card) => {
            const checkbox = card.querySelector('input[type="checkbox"]');
            if (!checkbox || !checkbox.checked) return;

            const priceSpan = card.querySelector(
              "span.lb-price-sale-price.money"
            );
            const priceText = priceSpan
              ? priceSpan.textContent.trim().replace("$", "")
              : null;
            const priceCents = priceText
              ? Math.round(parseFloat(priceText) * 100)
              : 0;

            const qtyEl = card.querySelector(".lb-qty-count");
            let qty = qtyEl ? qtyEl.valueAsNumber : NaN;
            if (isNaN(qty) || qty < 1) {
              qty = parseInt(qtyEl?.getAttribute?.("value") || "", 10);
            }
            if (isNaN(qty) || qty < 1) {
              qty = parseInt((qtyEl?.textContent || "").trim(), 10);
            }
            if (isNaN(qty) || qty < 1) qty = 1;

            totalCents += priceCents * qty;
          });
        return totalCents;
      }

      attachAddonPriceSync() {
        const onAddonChange = () => this.updateButtonText();

        // Checkbox toggles and an add-on's own quantity stepper both
        // bubble a change/click - delegated at the document level so this
        // keeps working no matter how many times the widget re-renders.
        document.addEventListener(
          "change",
          (event) => {
            if (
              event.target.closest &&
              event.target.closest(".lb-flat-card-host, .lb-product-card")
            ) {
              onAddonChange();
            }
          },
          true
        );

        document.addEventListener(
          "click",
          (event) => {
            if (
              event.target.closest &&
              event.target.closest(
                ".lb-qty-add, .lb-qty-remove, .lb-card-checkbox, .lb-checkbox-cont"
              )
            ) {
              // The widget updates its own DOM asynchronously after a tap,
              // so give it a beat before re-reading price/quantity.
              setTimeout(onAddonChange, 50);
            }
          },
          true
        );

        // Catches the widget re-rendering a card's price, quantity, or
        // selection state without a normal change/click event reaching us
        // - scoped to the add-on widget's own container rather than the
        // whole document to keep this cheap.
        let tries = 0;
        const attachObserver = () => {
          const target = document.querySelector(
            ".lb-embed, .lb-widget-pal"
          );
          if (!target) return false;
          new MutationObserver(() => onAddonChange()).observe(target, {
            childList: true,
            subtree: true,
            attributes: true,
          });
          return true;
        };
        if (!attachObserver()) {
          const poll = setInterval(() => {
            tries++;
            if (attachObserver() || tries > 200) clearInterval(poll);
          }, 100);
        }
      }

      // Safari (and some in-app browsers) can fail to submit a <button>
      // when the tap lands on nested text rather than the button's own
      // box - the theme already works around this for the main Add to Bag
      // button's price span (see the Safari forwarder in Custom.js); this
      // applies the same fix to this bar's button.
      attachSubmitPriceForwarder() {
        const forward = (event) => {
          const span = event.target.closest(".sticky-atc-bar__submit > span");
          if (!span) return;
          const button = span.closest('button[type="submit"]');
          if (!button || button.disabled) return;
          const form = button.closest("form");
          event.preventDefault();
          event.stopPropagation();
          if (form && typeof form.requestSubmit === "function") {
            form.requestSubmit(button);
          } else {
            button.click();
          }
        };
        document.addEventListener("click", forward, true);
        document.addEventListener("touchend", forward, {
          capture: true,
          passive: false,
        });
      }

      // ====== Forward Add to Bag to the main button ======
      // "Pairs Well With" style upsell widgets (e.g. Selleasy) hook the
      // MAIN Add to Bag button directly: checking their box and clicking
      // that button bundles the add-on into the same cart add. This bar's
      // own form only ever knows how to submit its own single line item,
      // so any such hook is invisible to it and a checked add-on would be
      // silently dropped.
      //
      // An earlier version of this tried to detect "is an add-on checked
      // right now" before deciding whether to forward the click. That was
      // unreliable in practice: these third-party widgets re-render their
      // own DOM independently (we observed the add-on cards drop back to
      // an unhydrated, un-checked shell state on their own), so a
      // synchronous check at the exact moment of the click could easily
      // read a stale or mid-render DOM and miss a genuinely checked box.
      //
      // Instead, this bar now always forwards its Add to Bag click to the
      // real main button rather than submitting itself. Quantity and
      // selling plan are already mirrored there (see the sync methods
      // above), so the result is always identical to the shopper clicking
      // the main button directly - add-on(s) included whenever one is
      // actually checked at that point, exactly as if they'd clicked the
      // main button themselves, with no separate detection step to get
      // out of sync.
      getMainAddButton() {
        const buttons = document.querySelectorAll('button[name="add"]');
        for (const el of buttons) {
          if (!el.closest(".sticky-atc-bar")) return el;
        }
        return null;
      }

      setOwnLoading(isLoading) {
        const button = this.querySelector(".sticky-atc-bar__submit");
        if (!button) return;
        if (isLoading) {
          button.classList.add("btn--loading");
          button.setAttribute("aria-disabled", "true");
        } else {
          button.classList.remove("btn--loading");
          button.removeAttribute("aria-disabled");
        }
      }

      attachStickySubmitForwarding() {
        const button = this.querySelector(".sticky-atc-bar__submit");
        if (!button) return;
        button.addEventListener(
          "click",
          (event) => {
            if (button.disabled || button.classList.contains("btn--loading")) {
              return;
            }

            const mainButton = this.getMainAddButton();
            // No main button to forward to (unexpected page structure) -
            // fall back to this bar's own normal single-item submission
            // rather than doing nothing.
            if (!mainButton || mainButton.disabled) return;

            // Stop this bar's own single-item submission - the main
            // button's click handler(s) (theme + any upsell app) run
            // instead, using state already mirrored here.
            event.preventDefault();
            event.stopPropagation();

            this.setOwnLoading(true);

            let settled = false;
            const finish = () => {
              if (settled) return;
              settled = true;
              clearTimeout(timeoutId);
              document.removeEventListener("product-ajax:added", finish);
              document.removeEventListener("product-ajax:error", finish);
              this.setOwnLoading(false);
            };
            // The theme's own product-form dispatches these on the
            // document after a submit settles (see product-form.js), so
            // this clears our loading state exactly when the real add
            // finishes - success or error - rather than guessing at a
            // fixed delay or polling the cart.
            document.addEventListener("product-ajax:added", finish);
            document.addEventListener("product-ajax:error", finish);
            // Safety net in case neither event fires for some reason.
            const timeoutId = setTimeout(finish, 8000);

            mainButton.click();
          },
          true
        );
      }
    }
  );
}